import { useCallback, useEffect, useMemo, useState } from "react";
import { MessageCircle, Mail, X, Send, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function DmModal({ open, onClose, peptide }) {
  const [contact, setContact] = useState({ phone: "", email: "" });

  useEffect(() => {
    let cancelled = false;
    api.get("/config/contact")
      .then((r) => { if (!cancelled) setContact({ phone: r.data.phone || "", email: r.data.email || "" }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const links = useMemo(() => {
    const peptideText = peptide
      ? `Hi, I'm interested in ${peptide.name}${peptide.vial_mg && peptide.vial_mg !== "stack" ? ` (${peptide.vial_mg}mg)` : ""}. Could you share availability and pricing?`
      : "Hi, I'm interested in a peptide. Can you help with sourcing?";
    const subject = peptide ? `PepIQ — inquiry about ${peptide.name}` : "PepIQ — peptide inquiry";
    return {
      wa: contact.phone ? `https://wa.me/${contact.phone}?text=${encodeURIComponent(peptideText)}` : "#",
      email: contact.email ? `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(peptideText)}` : "#",
    };
  }, [contact, peptide]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="dm-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0A0A0A] border border-[#262626] rounded-lg overflow-hidden">
        <ModalHeader title={`DM about ${peptide?.name || "peptides"}`} onClose={onClose} />
        <DmBody links={links} peptide={peptide} />
      </div>
    </div>
  );
}

function DmBody({ links, peptide }) {
  const [tab, setTab] = useState("quick");
  return (
    <>
      <TabBar tab={tab} onChange={setTab} />
      {tab === "quick" ? (
        <QuickDmTab links={links} />
      ) : (
        <InquiryForm key={peptide?.slug || "no-peptide"} peptide={peptide} />
      )}
    </>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-[#262626] bg-[#121212]">
      <div className="flex items-center gap-2">
        <MessageCircle size={18} className="text-[#CCFF00]" />
        <h3 className="font-heading font-bold text-lg">{title}</h3>
      </div>
      <button data-testid="dm-close" onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X size={18} /></button>
    </div>
  );
}

function TabBar({ tab, onChange }) {
  const tabs = [{ id: "quick", label: "Quick DM" }, { id: "form", label: "Send Inquiry" }];
  return (
    <div className="flex border-b border-[#262626]">
      {tabs.map((t) => (
        <button
          key={t.id}
          data-testid={`dm-tab-${t.id}`}
          onClick={() => onChange(t.id)}
          className={`flex-1 px-4 py-3 text-sm font-bold transition-colors ${tab === t.id ? "text-[#CCFF00] border-b-2 border-[#CCFF00]" : "text-[#A3A3A3] hover:text-white"}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function QuickDmTab({ links }) {
  return (
    <div className="p-6 space-y-3">
      <p className="text-sm text-[#A3A3A3] mb-2 font-mono-data">› Pick a channel — message is pre-filled.</p>
      <ContactRow
        href={links.wa}
        testid="dm-whatsapp"
        color="#25D366"
        Icon={MessageCircle}
        title="WhatsApp"
        subtitle="Open chat with pre-filled message"
        external
      />
      <ContactRow
        href={links.email}
        testid="dm-email"
        color="#CCFF00"
        Icon={Mail}
        title="Email"
        subtitle="Compose in your mail app"
      />
    </div>
  );
}

function ContactRow({ href, testid, color, Icon, title, subtitle, external }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
      className="flex items-center justify-between bg-[#121212] border border-[#262626] hover:border-[var(--c)]/60 rounded-md px-4 py-4 transition-colors group"
      style={{ "--c": color }}
    >
      <span className="flex items-center gap-3">
        <span className="w-10 h-10 grid place-items-center rounded" style={{ background: `${color}26`, color }}><Icon size={18} /></span>
        <span>
          <span className="block font-bold text-white">{title}</span>
          <span className="block text-xs text-[#A3A3A3] font-mono-data">{subtitle}</span>
        </span>
      </span>
      <Send size={16} className="text-[#A3A3A3]" />
    </a>
  );
}

function InquiryForm({ peptide }) {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    message: peptide ? `Hey — I'm interested in ${peptide.name}. Can you share availability and pricing?` : "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/inquiries", {
        peptide_slug: peptide?.slug || "",
        peptide_name: peptide?.name || "",
        ...form,
      });
      setDone(true);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  }, [form, peptide]);

  if (done) {
    return (
      <div data-testid="dm-form-success" className="p-6 text-center py-10">
        <div className="w-12 h-12 mx-auto grid place-items-center bg-[#CCFF00]/20 rounded-full mb-4">
          <Check size={24} className="text-[#CCFF00]" />
        </div>
        <h4 className="font-heading text-xl font-bold">Inquiry sent</h4>
        <p className="text-sm text-[#A3A3A3] mt-2 font-mono-data">We&apos;ll get back to you ASAP.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-6 space-y-4">
      <Field label="Your name">
        <input data-testid="dm-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
      </Field>
      <Field label="Email">
        <input data-testid="dm-form-contact" type="email" required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={inp} placeholder="So we can reply" />
      </Field>
      <Field label="Message">
        <textarea data-testid="dm-form-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inp} />
      </Field>
      {err && <p data-testid="dm-form-err" className="text-[#FF3366] text-sm font-mono-data">{err}</p>}
      <button data-testid="dm-form-submit" disabled={busy} className="w-full bg-[#CCFF00] text-black font-bold px-6 py-3 rounded-md hover:bg-[#B3E600] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
        {busy ? "Sending..." : <><Send size={14} /> Send inquiry</>}
      </button>
    </form>
  );
}

const inp = "w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-md px-4 py-3 focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] transition-all font-mono-data text-sm";
function Field({ label, children }) {
  return <div><label className="text-sm font-semibold text-[#A3A3A3] mb-2 block">{label}</label>{children}</div>;
}
